import React from "react";
import { Send, ArrowLeftCircle, FileUp, Truck, Calculator, Link2 } from "lucide-react";
import { Modal } from "./Modal";
import { SubcontractorTypeahead } from "./SubcontractorTypeahead";
import api, { apiErr } from "../lib/api";
import { money } from "../lib/format";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export const DELIVERY_METHODS = [
  { value: "pte_deliver", label: "PTE deliver" },
  { value: "courier", label: "Courier" },
  { value: "subcontractor_collect", label: "Subcontractor collect" },
  { value: "other", label: "Other" },
];
export const deliveryLabel = (v) =>
  (DELIVERY_METHODS.find((d) => d.value === v) || {}).label || (v ? v : "—");

function Field({ label, hint, required, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-3)", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#c0303a" }}> *</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3, display: "block" }}>{hint}</span>}
    </label>
  );
}

// ---- Mark Sent ------------------------------------------------------------
export function MarkSentModal({ rows, row: singleRow, onClose, onDone }) {
  rows = rows || (singleRow ? [singleRow] : []);
  const row = rows[0];
  const bulk = rows.length > 1;
  const [form, setForm] = React.useState({
    subcontractor_name: row?.subcontractor_name || "",
    sent_date: row?.sent_date || today(),
    expected_return_date: row?.expected_return_date || "",
    delivery_method: row?.delivery_method || "pte_deliver",
    subcontractor_reference: row?.subcontractor_reference || "",
  });
  const [busy, setBusy] = React.useState(false);

  const valid = form.subcontractor_name && form.sent_date;
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      for (const r of rows) {
        await api.patch(`/job-items/${r.id}/status`, { status: "at_subcontractor", ...form });
      }
      toast.success(bulk ? `${rows.length} items marked sent` : "Marked sent to subcontractor");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Send} title="Mark sent to subcontractor"
      subtitle={bulk ? `${rows.length} items selected` : `${row?.description || "Item"} · ${row?.delivery_note_number || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="marksent-cancel">Cancel</button>
        <button className="btn gold" onClick={submit} disabled={!valid || busy} data-testid="marksent-submit">
          <Send size={14} /> {busy ? "Saving…" : (bulk ? `Mark Sent (${rows.length})` : "Mark Sent")}
        </button>
      </>}>
      <Field label="Subcontractor" required>
        <SubcontractorTypeahead
          value={form.subcontractor_name}
          onChange={(v) => setForm({ ...form, subcontractor_name: v })}
          placeholder="Pick from history or enter new"
          testid="marksent-name" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Date sent" required>
          <input type="date" className="input" data-testid="marksent-sent-date" value={form.sent_date}
            onChange={(e) => setForm({ ...form, sent_date: e.target.value })} />
        </Field>
        <Field label="Expected return">
          <input type="date" className="input" data-testid="marksent-return-date" value={form.expected_return_date}
            onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })} />
        </Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Delivery method">
          <select className="input" data-testid="marksent-delivery-method" value={form.delivery_method}
            onChange={(e) => setForm({ ...form, delivery_method: e.target.value })}>
            {DELIVERY_METHODS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </Field>
        <Field label="Their reference / job no.">
          <input className="input" data-testid="marksent-reference" value={form.subcontractor_reference}
            onChange={(e) => setForm({ ...form, subcontractor_reference: e.target.value })}
            placeholder="e.g. 25631" />
        </Field>
      </div>
    </Modal>
  );
}

// ---- Mark Received Back ----------------------------------------------------
export function MarkReceivedModal({ rows, row: singleRow, onClose, onDone }) {
  rows = rows || (singleRow ? [singleRow] : []);
  const row = rows[0];
  const bulk = rows.length > 1;
  const [form, setForm] = React.useState({
    returned_date: today(),
    subcontractor_reference: row?.subcontractor_certificate_number || row?.subcontractor_reference || "",
    subcontractor_notes: "",
  });
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      for (const r of rows) {
        await api.patch(`/job-items/${r.id}/status`, {
          status: "returned_from_subcontractor",
          returned_date: form.returned_date || undefined,
          subcontractor_reference: form.subcontractor_reference || undefined,
          subcontractor_notes: form.subcontractor_notes || undefined,
        });
      }
      toast.success(bulk ? `${rows.length} items marked received back` : "Marked received back from subcontractor");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={ArrowLeftCircle} title="Mark received back"
      subtitle={bulk ? `${rows.length} items selected` : `${row?.description || "Item"} · ${row?.subcontractor_name || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="markrecv-cancel">Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy} data-testid="markrecv-submit">
          <ArrowLeftCircle size={14} /> {busy ? "Saving…" : (bulk ? `Mark Received Back (${rows.length})` : "Mark Received Back")}
        </button>
      </>}>
      <p style={{ fontSize: 12.5, color: "var(--text-2)", marginTop: 0, marginBottom: 14 }}>
        {bulk
          ? `Records ${rows.length} items as back in PTE's possession. The values below apply to all selected items.`
          : "Records the item as back in PTE's possession. All fields are optional."}
      </p>
      <Field label="Returned date">
        <input type="date" className="input" data-testid="markrecv-date" value={form.returned_date}
          onChange={(e) => setForm({ ...form, returned_date: e.target.value })} />
      </Field>
      <Field label="Subcontractor certificate / reference">
        <input className="input" data-testid="markrecv-reference" value={form.subcontractor_reference}
          onChange={(e) => setForm({ ...form, subcontractor_reference: e.target.value })}
          placeholder="Their cert / reference number" />
      </Field>
      <Field label="Notes">
        <textarea className="input" rows={3} data-testid="markrecv-notes" value={form.subcontractor_notes}
          onChange={(e) => setForm({ ...form, subcontractor_notes: e.target.value })}
          placeholder="Anything to note on return…" />
      </Field>
    </Modal>
  );
}

// ---- Upload Certificate ----------------------------------------------------
export function UploadCertModal({ rows, row: singleRow, onClose, onDone }) {
  rows = rows || (singleRow ? [singleRow] : []);
  const row = rows[0];
  const bulk = rows.length > 1;
  const [certNo, setCertNo] = React.useState(row?.subcontractor_certificate_number || "");
  const [notes, setNotes] = React.useState("");
  const [file, setFile] = React.useState(null);
  const [visible, setVisible] = React.useState(
    row?.subcontractor_certificate_visible_to_customers !== false);
  const [busy, setBusy] = React.useState(false);

  const valid = certNo.trim() || file;
  const submit = async () => {
    if (!valid) { toast.error("Add a certificate number or attach a file."); return; }
    setBusy(true);
    try {
      for (const r of rows) {
        const fd = new FormData();
        if (certNo.trim()) fd.append("certificate_number", certNo.trim());
        if (notes.trim()) fd.append("notes", notes.trim());
        fd.append("visible_to_customers", visible ? "true" : "false");
        if (file) fd.append("file", file);
        await api.post(`/job-items/${r.id}/subcontractor-certificate`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(bulk ? `Certificate saved to ${rows.length} items` : "Subcontractor certificate saved");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={FileUp} title="Subcontractor certificate"
      subtitle={bulk ? `${rows.length} items selected` : `${row?.description || "Item"} · ${row?.subcontractor_name || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="uploadcert-cancel">Cancel</button>
        <button className="btn gold" onClick={submit} disabled={!valid || busy} data-testid="uploadcert-submit">
          <FileUp size={14} /> {busy ? "Saving…" : (bulk ? `Save to ${rows.length} items` : "Save certificate")}
        </button>
      </>}>
      {bulk && (
        <div className="card pad" style={{ background: "#fff8ec", border: "1px solid #e3d28a",
          fontSize: 12.5, marginBottom: 14 }}>
          The certificate number, file and visibility below will be applied to <strong>all {rows.length}</strong> selected items.
        </div>
      )}
      {row?.certificate_number && (
        <div className="card pad" style={{ background: "#f4f7fb", border: "1px solid #d4e0ee",
          fontSize: 12.5, marginBottom: 14 }}>
          PTE certificate for this item: <span className="mono t-strong">{row.certificate_number}</span>.
          The subcontractor's certificate below is recorded separately — PTE numbering is never changed.
        </div>
      )}
      <Field label="Subcontractor certificate number">
        <input className="input" data-testid="uploadcert-number" value={certNo}
          onChange={(e) => setCertNo(e.target.value)} placeholder="Their certificate number" />
      </Field>
      <Field label="Certificate file (PDF / photo)" hint="On a phone this opens the camera or photo library.">
        <input type="file" className="input" data-testid="uploadcert-file"
          accept="application/pdf,image/*" capture="environment"
          onChange={(e) => setFile(e.target.files?.[0] || null)} />
        {file && <span style={{ fontSize: 11.5, color: "var(--text-2)" }}>{file.name}</span>}
      </Field>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 10 }}>
        <input type="checkbox" checked={visible} data-testid="uploadcert-visible"
          onChange={(e) => setVisible(e.target.checked)} />
        Visible to the customer (shows in their portal certificate history)
      </label>
      <Field label="Notes">
        <textarea className="input" rows={2} data-testid="uploadcert-notes" value={notes}
          onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </Field>
    </Modal>
  );
}

// ---- Subcontractor costing (internal — manager/admin only) -----------------
export function CostingModal({ rows, row: singleRow, onClose, onDone }) {
  const row = (rows && rows[0]) || singleRow;
  const [form, setForm] = React.useState({
    subcon_quoted_cost: "", subcon_actual_cost: "", subcon_markup_pct: "",
    subcon_quote_override: "", subcon_quote_number: "", subcon_po_number: "",
    subcon_invoice_number: "",
  });
  const [defaultPct, setDefaultPct] = React.useState(45);
  const [busy, setBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    api.get(`/job-items/${row.id}/subcontractor-costing`).then((r) => {
      const d = r.data || {};
      setDefaultPct(d.default_markup_pct ?? 45);
      setForm({
        subcon_quoted_cost: d.subcon_quoted_cost ?? "",
        subcon_actual_cost: d.subcon_actual_cost ?? "",
        subcon_markup_pct: d.subcon_markup_pct ?? "",
        subcon_quote_override: d.subcon_quote_override ?? "",
        subcon_quote_number: d.subcon_quote_number ?? "",
        subcon_po_number: d.subcon_po_number ?? "",
        subcon_invoice_number: d.subcon_invoice_number ?? "",
      });
      setLoaded(true);
    }).catch((e) => { toast.error(apiErr(e)); setLoaded(true); });
  }, [row.id]);

  const num = (v) => (v === "" || v === null ? null : Number(v));
  const effPct = form.subcon_markup_pct !== "" ? num(form.subcon_markup_pct) : defaultPct;
  const base = form.subcon_actual_cost !== "" ? num(form.subcon_actual_cost)
    : (form.subcon_quoted_cost !== "" ? num(form.subcon_quoted_cost) : null);
  const suggested = base != null ? Math.round(base * (1 + (effPct || 0) / 100) * 100) / 100 : null;
  const finalQuote = form.subcon_quote_override !== "" ? num(form.subcon_quote_override) : suggested;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {};
      ["subcon_quoted_cost", "subcon_actual_cost", "subcon_markup_pct", "subcon_quote_override"]
        .forEach((k) => { payload[k] = form[k] === "" ? null : Number(form[k]); });
      ["subcon_quote_number", "subcon_po_number", "subcon_invoice_number"]
        .forEach((k) => { payload[k] = form[k] || null; });
      await api.put(`/job-items/${row.id}/subcontractor-costing`, payload);
      toast.success("Subcontractor costing saved");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const Num = (k, label, hint) => (
    <Field label={label} hint={hint}>
      <input className="input mono" type="number" step="0.01" data-testid={`costing-${k}`}
        value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder="0.00" />
    </Field>
  );
  const Txt = (k, label) => (
    <Field label={label}>
      <input className="input" data-testid={`costing-${k}`} value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
    </Field>
  );

  return (
    <Modal open onClose={onClose} icon={Calculator} title="Subcontractor costing"
      subtitle={`${row?.description || "Item"} · ${row?.subcontractor_name || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="costing-cancel">Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy || !loaded} data-testid="costing-submit">
          <Calculator size={14} /> {busy ? "Saving…" : "Save costing"}
        </button>
      </>}>
      <div className="card pad" style={{ background: "#fff8ec", border: "1px solid #e3d28a",
        fontSize: 12, marginBottom: 14 }}>
        <strong>Internal only.</strong> Not shown to technicians or customers, and not accounting —
        Sage stays the system of record. This just suggests a PTE quote from the subcontractor cost.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {Num("subcon_quoted_cost", "Subcontractor quoted cost")}
        {Num("subcon_actual_cost", "Subcontractor actual cost")}
        {Num("subcon_markup_pct", "Markup %", `Default ${defaultPct}% if blank`)}
        {Num("subcon_quote_override", "PTE quote override", "Optional manual amount")}
      </div>

      <div className="card pad" style={{ background: "var(--card-2)", margin: "8px 0 14px",
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }} data-testid="costing-summary">
        <div><div className="t-mut" style={{ fontSize: 10.5, textTransform: "uppercase" }}>Base cost</div>
          <div className="mono t-strong">{base != null ? money(base) : "—"}</div></div>
        <div><div className="t-mut" style={{ fontSize: 10.5, textTransform: "uppercase" }}>Suggested @ {effPct || 0}%</div>
          <div className="mono t-strong" data-testid="costing-suggested">{suggested != null ? money(suggested) : "—"}</div></div>
        <div><div className="t-mut" style={{ fontSize: 10.5, textTransform: "uppercase" }}>PTE quote</div>
          <div className="mono t-strong" style={{ color: "#8a5a00" }} data-testid="costing-final">{finalQuote != null ? money(finalQuote) : "—"}</div></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {Txt("subcon_quote_number", "Quote no. (Sage)")}
        {Txt("subcon_po_number", "PO no.")}
        {Txt("subcon_invoice_number", "Invoice no.")}
      </div>
    </Modal>
  );
}

// ---- Link to Subcon (fix a legacy free-text / stale subcontractor link) --
export function EditSubconLinkModal({ rows, row: singleRow, onClose, onDone }) {
  rows = rows || (singleRow ? [singleRow] : []);
  const row = rows[0];
  const bulk = rows.length > 1;
  const [subcontractorId, setSubcontractorId] = React.useState("");
  const [subcontractorName, setSubcontractorName] = React.useState(bulk ? "" : (row?.subcontractor_name || ""));
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!subcontractorId) { toast.error("Search and pick an active subcontractor from the list"); return; }
    setBusy(true);
    try {
      for (const r of rows) {
        await api.patch(`/job-items/${r.id}/subcontractor-link`, { subcontractor_id: subcontractorId });
      }
      toast.success(bulk ? `${rows.length} items linked to the active register record` : "Linked to the active register record");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Link2} title="Link to Subcon"
      subtitle={bulk ? `${rows.length} items selected` : `${row?.description || "Item"} · ${row?.delivery_note_number || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="editsubconlink-cancel">Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy || !subcontractorId} data-testid="editsubconlink-submit">
          <Link2 size={14} /> {busy ? "Saving…" : (bulk ? `Save link (${rows.length})` : "Save link")}
        </button>
      </>}>
      <p style={{ fontSize: 12.5, color: "var(--text-2)", marginTop: 0, marginBottom: 14 }}>
        {bulk
          ? `Assigns the subcontractor you pick below to all ${rows.length} selected items on this delivery note.`
          : <>Currently recorded as <strong>{row?.subcontractor_name || "—"}</strong>.</>} Search and select the
        correct ACTIVE register record below so its address, contact and details pull through
        correctly on delivery notes and certificates.
      </p>
      <Field label="Subcontractor" required>
        <SubcontractorTypeahead
          value={subcontractorName}
          onChange={(v) => { setSubcontractorName(v); setSubcontractorId(""); }}
          onPick={(sc) => { setSubcontractorId(sc.id); setSubcontractorName(sc.name); }}
          activeOnly allowFreeText={false}
          placeholder="Type to search active subcontractors…"
          testid="editsubconlink-search" />
      </Field>
    </Modal>
  );
}

// ---- Edit shared subcontractor details (reference / delivery / return date) --
export function EditSubconDetailsModal({ rows, onClose, onDone }) {
  const bulk = rows.length > 1;
  const first = rows[0];
  const [form, setForm] = React.useState({
    subcontractor_reference: bulk ? "" : (first?.subcontractor_reference || ""),
    delivery_method: bulk ? "" : (first?.delivery_method || ""),
    expected_return_date: bulk ? "" : (first?.expected_return_date || ""),
  });
  const [busy, setBusy] = React.useState(false);

  const valid = form.subcontractor_reference || form.delivery_method || form.expected_return_date;
  const submit = async () => {
    if (!valid) { toast.error("Change at least one field before saving."); return; }
    const payload = {};
    if (form.subcontractor_reference) payload.subcontractor_reference = form.subcontractor_reference;
    if (form.delivery_method) payload.delivery_method = form.delivery_method;
    if (form.expected_return_date) payload.expected_return_date = form.expected_return_date;
    setBusy(true);
    try {
      for (const r of rows) {
        await api.patch(`/job-items/${r.id}/subcontractor-details`, payload);
      }
      toast.success(bulk ? `Updated details on ${rows.length} items` : "Subcontractor details updated");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Truck} title="Edit subcontractor details"
      subtitle={bulk ? `${rows.length} items on this delivery note` : `${first?.description || "Item"} · ${first?.delivery_note_number || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="editsubcondetails-cancel">Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy || !valid} data-testid="editsubcondetails-submit">
          <Truck size={14} /> {busy ? "Saving…" : (bulk ? `Save to ${rows.length} items` : "Save")}
        </button>
      </>}>
      <p style={{ fontSize: 12.5, color: "var(--text-2)", marginTop: 0, marginBottom: 14 }}>
        Only fields you change below are applied{bulk ? ` — to all ${rows.length} selected items` : ""}. This does not
        change status/stage, just corrects the recorded details.
      </p>
      <Field label="Their reference / job no.">
        <input className="input" data-testid="editsubcondetails-reference" value={form.subcontractor_reference}
          onChange={(e) => setForm({ ...form, subcontractor_reference: e.target.value })}
          placeholder={bulk ? "Leave blank to keep each item's current value" : "e.g. 25631"} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Delivery method">
          <select className="input" data-testid="editsubcondetails-delivery-method" value={form.delivery_method}
            onChange={(e) => setForm({ ...form, delivery_method: e.target.value })}>
            <option value="">{bulk ? "— keep current —" : "— select —"}</option>
            {DELIVERY_METHODS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </Field>
        <Field label="Expected return">
          <input type="date" className="input" data-testid="editsubcondetails-return-date" value={form.expected_return_date}
            onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

// ---- In Dispatch (confirm) ------------------------------------------
export function ReadyDispatchModal({ rows, row: singleRow, onClose, onDone }) {
  rows = rows || (singleRow ? [singleRow] : []);
  const row = rows[0];
  const bulk = rows.length > 1;
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      for (const r of rows) {
        await api.patch(`/job-items/${r.id}/status`, { status: "ready_for_delivery" });
      }
      toast.success(bulk ? `${rows.length} items moved to In Dispatch` : "Moved to In Dispatch");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const anyMissingCert = rows.some((r) => !r?.subcontractor_certificate_number);
  return (
    <Modal open onClose={onClose} icon={Truck} title="In Dispatch?"
      subtitle={bulk ? `${rows.length} items selected` : `${row?.description || "Item"} · ${row?.delivery_note_number || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="readydispatch-cancel">Cancel</button>
        <button className="btn gold" onClick={submit} disabled={busy} data-testid="readydispatch-submit">
          <Truck size={14} /> {busy ? "Saving…" : (bulk ? `Confirm (${rows.length})` : "Confirm — In Dispatch")}
        </button>
      </>}>
      <p style={{ fontSize: 13, color: "var(--text-2)", margin: 0 }}>
        This moves {bulk ? `${rows.length} items` : "the item"} out of the subcontractor queue into <strong>In Dispatch</strong>,
        and notifies the customer that calibration is complete.
        {anyMissingCert && (
          <span style={{ display: "block", marginTop: 8, color: "#a35a00" }}>
            {bulk ? "Some selected items have no subcontractor certificate recorded yet" : "No subcontractor certificate recorded yet"} — you can still proceed, or upload it first.
          </span>
        )}
      </p>
    </Modal>
  );
}
